import { useEffect, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Button, Card, CardContent } from '@jeraisonne/ui';
import { CheckCircle2, UserPlus, X } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useAcceptPeerInvite } from '@/lib/student/peers';

/**
 * Cible des liens d'invitation peer partagés (URL ou QR code).
 * Affiche une page de confirmation avant d'activer le lien : la migration 0073
 * exige un clic explicite de l'invité ("doit être acceptée").
 */
export function AcceptPeerInvitePage() {
  const { code } = useParams({ strict: false }) as { code: string };
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const studentId = user?.id ?? '';
  const accept = useAcceptPeerInvite(studentId);
  const [result, setResult] = useState<{ name: string } | null>(null);

  // Sécurité minimale : on s'assure qu'on est bien connecté en tant qu'élève.
  // Les autres rôles ne devraient pas recevoir ce lien — on les renvoie au home.
  useEffect(() => {
    if (profile && profile.role !== 'student') {
      navigate({ to: '/' });
    }
  }, [profile, navigate]);

  function handleAccept() {
    accept.mutate(code, {
      onSuccess: (res) => setResult({ name: res.peer_display_name }),
    });
  }

  return (
    <div className="pb-safe mx-auto max-w-md px-4 py-6 sm:py-10">
      <Card className="border-ink-100">
        <CardContent className="space-y-5 py-6">
          {result ? (
            <>
              <div className="flex flex-col items-center text-center">
                <CheckCircle2 className="mb-2 h-12 w-12 text-emerald-500" />
                <h1 className="font-display text-ink-900 text-xl font-extrabold">
                  C'est fait&nbsp;!
                </h1>
                <p className="text-ink-500 mt-1 text-sm">
                  <span className="text-ink-900 font-semibold">{result.name}</span> est maintenant
                  ton camarade. Tu verras ses activités dans tes actualités.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <Button className="w-full" onClick={() => navigate({ to: '/student/contacts' })}>
                  Voir mes contacts
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate({ to: '/student/news' })}
                >
                  Aller aux actualités
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col items-center text-center">
                <div className="bg-primary-50 mb-2 flex h-14 w-14 items-center justify-center rounded-full">
                  <UserPlus className="text-primary-600 h-7 w-7" />
                </div>
                <h1 className="font-display text-ink-900 text-xl font-extrabold">
                  Ajouter un camarade
                </h1>
                <p className="text-ink-500 mt-1 text-sm">
                  Quelqu'un t'a partagé son lien d'invitation. En l'acceptant, vous deviendrez
                  camarades sur JeRaisonne et pourrez vous défier sur les SKILLCARDS.
                </p>
                <p className="text-ink-400 mt-2 font-mono text-xs tracking-widest">Code : {code}</p>
              </div>

              {accept.isError && (
                <p className="text-accent-600 text-center text-sm">
                  {(accept.error as Error).message}
                </p>
              )}

              <div className="flex flex-col gap-2">
                <Button
                  className="w-full"
                  onClick={handleAccept}
                  disabled={accept.isPending || !studentId}
                >
                  {accept.isPending ? 'Validation…' : 'Accepter'}
                </Button>
                <Button
                  variant="outline"
                  className="w-full gap-1.5"
                  onClick={() => navigate({ to: '/student/contacts' })}
                >
                  <X className="h-4 w-4" />
                  Refuser
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
